import { useState } from "react";
import { ArrowRight, CheckCircle2, X, Loader2, Sparkles } from "lucide-react";
import { Link } from "react-router-dom";
import { useLang } from "../../lib/LangContext";
import { useLocalePath } from "./LangRouting";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export const Newsletter = () => {
  const { t, lang } = useLang();
  const lp = useLocalePath();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [consent, setConsent] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [error, setError] = useState("");

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email || !consent || submitting) return;
    setSubmitting(true);
    setError("");
    try {
      const res = await fetch(`${BACKEND_URL}/api/newsletter/subscribe`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          email,
          name: name || null,
          language: lang,
          source_page: typeof window !== "undefined" ? window.location.pathname : "/",
          consent_gdpr: true,
        }),
      });
      if (!res.ok) throw new Error("Subscription failed");
      setEmail("");
      setName("");
      setConsent(false);
      setShowSuccess(true);
    } catch {
      setError(t.promotions.error);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section
      id="promotions"
      data-testid="newsletter-section"
      className="relative py-12 md:py-16 bg-[#0A0A0C] border-t border-white/5"
    >
      <div className="max-w-[1440px] mx-auto px-5 sm:px-6 lg:px-12 grid lg:grid-cols-12 gap-6 lg:gap-12 items-start">
        <div className="lg:col-span-5">
          <span className="inline-flex items-center gap-2 text-[12px] tracking-[0.3em] uppercase text-gold">
            <Sparkles size={12} /> {t.promotions.eyebrow}
          </span>
          <h2 className="mt-3 font-heading text-white text-xl md:text-2xl tracking-tight">{t.promotions.title}</h2>
          <p className="mt-2 text-zinc-400 text-[14px] leading-relaxed max-w-md">{t.promotions.desc}</p>
        </div>
        <form onSubmit={handleSubmit} data-testid="newsletter-form" className="lg:col-span-7 flex flex-col gap-3">
          <div className="flex flex-col sm:flex-row gap-2">
            <input
              type="text"
              data-testid="newsletter-name"
              aria-label={t.promotions.nameLabel}
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder={t.promotions.nameLabel}
              className="sm:w-[34%] bg-black/40 border border-white/15 px-4 py-3 text-white placeholder:text-zinc-600 focus:outline-none focus:border-gold transition-colors text-[14px]"
            />
            <input
              type="email"
              required
              data-testid="newsletter-input"
              aria-label={t.promotions.emailPlaceholder}
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder={t.promotions.emailPlaceholder}
              className="flex-1 bg-black/40 border border-white/15 px-4 py-3 text-white placeholder:text-zinc-600 focus:outline-none focus:border-gold transition-colors text-[14px]"
            />
            <button
              type="submit"
              disabled={submitting || !consent || !email}
              data-testid="newsletter-submit"
              className="inline-flex items-center justify-center gap-2 border border-gold/70 text-gold px-6 py-3 text-[12px] tracking-[0.2em] uppercase font-medium hover:bg-gold hover:text-black transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {submitting ? <Loader2 size={14} className="animate-spin" /> : <ArrowRight size={14} />}
              {t.promotions.cta}
            </button>
          </div>
          <label data-testid="newsletter-gdpr" className="flex items-start gap-3 text-left text-zinc-500 text-[12px] leading-relaxed cursor-pointer select-none">
            <input
              type="checkbox"
              required
              checked={consent}
              onChange={(e) => setConsent(e.target.checked)}
              data-testid="newsletter-gdpr-checkbox"
              className="mt-0.5 w-4 h-4 accent-[#d4af37] bg-black border border-white/20 cursor-pointer flex-shrink-0"
            />
            <span>
              {t.promotions.gdpr}{" "}
              <Link to={lp("/datenschutz")} className="text-gold hover:underline">{t.footer.datenschutz}</Link>.
            </span>
          </label>
          {error && <p data-testid="newsletter-error" className="text-red-400 text-[13px]">{error}</p>}
        </form>
      </div>

      {showSuccess && (
        <div
          data-testid="newsletter-success"
          className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/80 backdrop-blur-md"
          onClick={() => setShowSuccess(false)}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            className="relative w-full max-w-md bg-[#0a0a0c] border border-gold/40 p-8 md:p-10 shadow-[0_0_80px_-20px_rgba(212,175,55,0.5)]"
          >
            <button
              type="button"
              onClick={() => setShowSuccess(false)}
              data-testid="newsletter-success-close"
              className="absolute top-4 right-4 text-zinc-400 hover:text-gold"
              aria-label="close"
            >
              <X size={18} />
            </button>
            <div className="flex items-center gap-3">
              <CheckCircle2 size={28} className="text-gold" />
              <h3 className="font-heading text-white text-xl md:text-2xl uppercase tracking-tight">
                {t.promotions.successTitle}
              </h3>
            </div>
            <span className="block w-12 h-px bg-gold mt-5" />
            <p className="mt-6 text-zinc-300 text-sm leading-relaxed">{t.promotions.successMessage}</p>
            <button
              type="button"
              onClick={() => setShowSuccess(false)}
              data-testid="newsletter-success-ok"
              className="mt-8 inline-flex items-center justify-center gap-2 bg-gold text-black px-6 py-3 text-xs tracking-[0.25em] uppercase font-semibold hover:bg-[#f3e5ab] transition-colors w-full"
            >
              OK
            </button>
          </div>
        </div>
      )}
    </section>
  );
};
